import { useEffect, useRef, useState } from 'react';

// The value card's font sizing, measured on the ref's element rather than the
// whole widget.
export function useValueFontSize(text, isSmallWidget) {
    const
        ref = useRef(null),
        [fontSize, setFontSize] = useState(12);

    useEffect(() => {
        const element = ref.current;

        if (!element) {
            return;
        }

        const update = () => {
            const
                characterCount = Math.max(text.length, 5),
                baseDimension = isSmallWidget
                    ? Math.min(element.offsetWidth, element.offsetHeight)
                    : element.offsetWidth,
                multiplier = isSmallWidget ? 1.1 : 1.35;

            setFontSize(Math.min(80, Math.max(12, (baseDimension / characterCount) * multiplier)));
        };

        update();

        const observer = new ResizeObserver(update);

        observer.observe(element);

        return () => observer.disconnect();
    }, [text, isSmallWidget]);

    return [ref, fontSize];
}

const LINE_HEIGHT = 1.25;

const WIDTH_MARGIN = 0.95;

let measuringContext;

// Measured, not estimated: character widths vary too much for a guess to fill
// the space without overflowing it.
function boldWidthAt100(text, element) {
    measuringContext ??= document.createElement('canvas').getContext('2d');
    measuringContext.font = `bold 100px ${getComputedStyle(element).fontFamily}`;

    return measuringContext.measureText(text).width;
}

// One line at the largest size that fits. The 10 px floor (12 on the value card)
// lets a long value such as "13 456 789,7 m³" fit a minimum-size widget.
export function useFittedHeadline(text) {
    const
        ref = useRef(null),
        [fontSize, setFontSize] = useState(12);

    useEffect(() => {
        const element = ref.current;

        if (!element || !text) {
            return;
        }

        const update = () => {
            const
                byWidth = element.offsetWidth * WIDTH_MARGIN / boldWidthAt100(text, element) * 100,
                byHeight = element.offsetHeight * 0.7 / LINE_HEIGHT;

            setFontSize(Math.min(80, Math.max(10, Math.min(byWidth, byHeight))));
        };

        update();

        const observer = new ResizeObserver(update);

        observer.observe(element);

        return () => observer.disconnect();
    }, [text]);

    return [ref, fontSize];
}
